<script setup lang="ts">
/* eslint-disable max-lines -- Keep the template subview and DNC guards in the existing modal. */
import { computed, nextTick, ref, watch } from 'vue';

import { OutreachService } from '@/core/sourcing/outreach/outreach.service';
import { EmployerService } from '@/core/shared/employer/employer.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { MessagingService } from '@/core/sourcing/messaging/messaging.service';
import JobApplicantService from '@/core/shared/job-applicant/job-applicant.service';
import MeService from '@/core/shared/me/me.service';
import { computeRecentProOutreachWarning } from '@/core/sourcing/outreach/recent-pro-outreach-warning';
import type { RecentProOutreachPro } from '@/core/sourcing/outreach/recent-pro-outreach-warning';

import {
  type JobApplicant,
  PolicyEnforcement,
  PolicyName,
  type Project,
  type UserProfile,
} from '@factoryfixinc/ats-interfaces';
import { MessagingSource } from '@/core/sourcing/messaging/types/messaging-source.type';

import MessageSentAnimation from '@/components/Sourcing/MessageSentAnimation.vue';
import { ErrorService } from '@/core/shared/errors/error.service';
import Autocomplete from '@/components/Shared/Input/Autocomplete/Autocomplete.vue';
import BaseModal from '../Shared/Modals/BaseModal.vue';
import Checkbox from '../Shared/Input/Checkbox/Checkbox.vue';
import Divider from '../Shared/Divider/Divider.vue';
import RecentProOutreachWarning from './RecentProOutreachWarning.vue';
import TemplateLibrary from '@/components/Conversations/Templates/TemplateLibrary.vue';
import PlusCircleSvg from '@/assets/svg/plus-circle-20.svg?component';
import TrackingService from '@/core/shared/tracking/tracking.service';
import type { MessageTemplate } from '@/core/conversations/template/template.type';
import { getOutboundNameForCandidate } from '@/core/sourcing/search/utils/get-name-for-candidate.util';

import { pluralize } from '@/utils/strings/pluralize.util';
import { InternalError } from '@/core/shared/errors/internal.error';
import { useProjectSelector } from '@/composables/useProjectSelector';
import { useMessageTemplateSubview } from '@/composables/useMessageTemplateSubview';
import { ProjectPersistence } from '@/core/shared/project/project.persistence';

// Service instantiation
// ===============================
const messagingService = new MessagingService();
const outreachService = new OutreachService();
const employerService = new EmployerService();
const jobApplicantService = new JobApplicantService();
const meService = new MeService();
const projectPersistence = new ProjectPersistence();

// Emits
// ===============================
const emit = defineEmits<{
  (e: 'submit'): void;
  (e: 'close', value: { messageSent: boolean }): void;
  (e: 'remove-pro', value: number): void;
}>();

// Props
// ===============================
const props = withDefaults(
  defineProps<{
    open: boolean;
    showMessageField?: boolean;
    excludedJobIds?: number[];
    pros: Array<
      Pick<UserProfile, 'id' | 'nameFirst' | 'nameLast'> & {
        source?: MessagingSource;
        doNotContact?: boolean;
      }
    >;
    // Preselects a job when opened with context (e.g. from an active Talent Search job).
    // The picker stays fully editable; if this job isn't in `projects` (e.g. excluded via
    // `excludedJobIds`), the picker just shows no selection rather than blocking.
    initialJobId?: number;
    searchSessionId?: string;
  }>(),
  {
    excludedJobIds: () => [],
    pros: () => [],
  },
);

// Reactive state
// ===============================
const message = ref('');
const messageSentFlag = ref(false);
const showMore = ref(false);
const sendingMessage = ref(false);
const selectedJobId = ref<number | undefined>();
const aiOutreachEnabled = ref(false);
const excludeFlaggedCandidates = ref(false);
const messageTextAreaEl = ref<HTMLTextAreaElement | null>(null);

// While a template subview is showing it covers the Add to Job form, which stays mounted
// (v-show) so job, message, AI toggle and repeat-outreach choices survive by construction.
const templateSubview = useMessageTemplateSubview({
  formTitle: () => `Add ${pluralize(props.pros.length, 'candidate')} to job`,
});
const templateLibrary = ref<InstanceType<typeof TemplateLibrary> | null>(null);

// Applicant history for the currently-selected pros, held locally rather than read from the
// shared job-applicant store (which search/ProPreviewCard also overwrite). `fetchToken`
// discards a stale in-flight fetch when the modal is reopened for a different selection.
const selectedProsJobApplicants = ref<JobApplicant[]>([]);
let fetchToken = 0;
// Bumped by resetForm() (every close) so a resolveInitialJob() fetch that's still in flight
// when the recruiter cancels can't write a stale job preselect after the fact — into either
// this now-closed session or, worse, a later card/bulk open that reuses this same instance.
let jobPreselectToken = 0;
const {
  projectSearch,
  myJobsFilter,
  copilotJobsFilter,
  atsJobsFilter,
  projects,
  isLoadingProjects,
  fetchProjects,
  renderProject,
  resetFilters,
} = useProjectSelector({ getExcludedJobIds: () => props.excludedJobIds });

// Computed properties
// ===============================
const computedOpen = computed(() => props.open);
const recipientsCount = computed(() => props.pros.length);

const selectedRecentProOutreachPros = computed<RecentProOutreachPro[]>(() =>
  props.pros.map((pro) => ({
    profileId: pro.id,
    nameFirst: pro.nameFirst,
    nameLast: pro.nameLast,
  })),
);

const recentProOutreachWarning = computed(() =>
  computeRecentProOutreachWarning(
    selectedRecentProOutreachPros.value,
    (profileId) =>
      selectedProsJobApplicants.value.filter((applicant) => applicant.userProfileId === profileId),
    { now: new Date() },
  ),
);

const showRecentProOutreachWarning = computed(
  () => recentProOutreachWarning.value.flagged.length > 0,
);

const flaggedProfileIds = computed(
  () => new Set(recentProOutreachWarning.value.flagged.map((f) => f.profileId)),
);

const prosWithOutreachApproved = computed(() => {
  if (recentProOutreachWarning.value.someFlagged && excludeFlaggedCandidates.value) {
    return props.pros.filter((pro) => !flaggedProfileIds.value.has(Number(pro.id)));
  }
  return props.pros;
});

const hasDoNotContactRecipient = computed(() =>
  props.pros.some((pro) => pro.doNotContact === true),
);

const selectedProject = computed<Project | undefined>(() => {
  return projects.value.find((project) => project.jobId === selectedJobId.value);
});

// Checks selectedProject, not selectedJobId — a picker refetch (search, filter toggle)
// replaces `projects` wholesale, which can drop a still-selected job (e.g. one seeded via
// resolveInitialJob's off-page fetch) out from under `selectedJobId`. Keying on the id alone
// would leave Send enabled with no matching project, and sendMessage() has nothing to send.
const disableSendButton = computed(() => {
  return (
    !selectedProject.value ||
    !prosWithOutreachApproved.value.length ||
    hasDoNotContactRecipient.value
  );
});

const isSelectedJobActive = computed<boolean>(() => {
  return selectedProject.value?.copilot === true;
});

const isActiveOutreachEnabled = computed<boolean>(() => {
  return selectedProject.value?.activeOutreachEnabled === true;
});

const isSourcingGuardrailRestricted = computed<boolean>(() => {
  const policy = employerService.getPolicyByName(PolicyName.COPILOT_OUTREACH);
  return policy === PolicyEnforcement.FORBIDDEN;
});

const canEnableAIOutreach = computed<boolean>(() => {
  return (
    selectedJobId.value !== undefined &&
    isSelectedJobActive.value &&
    isActiveOutreachEnabled.value &&
    !isSourcingGuardrailRestricted.value &&
    !hasDoNotContactRecipient.value
  );
});

const aiOutreachTooltipText = computed<string>(() => {
  if (!selectedJobId.value) {
    return 'Select a job first';
  }
  if (!isSelectedJobActive.value) {
    return 'Only available for active jobs';
  }
  if (!isActiveOutreachEnabled.value) {
    return (
      'This job is set to Active job seekers only. ' +
      'Update the sourcing strategy to Active job seekers + passive candidates to enable.'
    );
  }
  if (isSourcingGuardrailRestricted.value) {
    return `This option is restricted by your company's settings. Contact your admin for more information.`;
  }
  return '';
});

const messageFieldDisabled = computed<boolean>(() => {
  return hasDoNotContactRecipient.value || (aiOutreachEnabled.value && canEnableAIOutreach.value);
});

// Templates only make sense for a manual first message.
const canUseTemplates = computed<boolean>(() => {
  return props.showMessageField === true && !messageFieldDisabled.value;
});

// Personalized previews need explicit values: with no selected conversation,
// mapTemplatePlaceholders would otherwise fall back to the conversation index and render
// some unrelated candidate's name. Empty strings (never undefined) keep that fallback out.
// The name is built the same way the send builds it, so the preview is what goes out.
const templatePreviewContext = computed(() => {
  const outboundName =
    props.pros.length === 1
      ? getOutboundNameForCandidate(props.pros[0])
      : { fullName: '', firstName: '' };

  return {
    proFullName: outboundName.fullName,
    proFirstName: outboundName.firstName,
    jobTitle: selectedProject.value?.title ?? '',
  };
});

// One recipient gets a preview with their own name; a bulk add keeps the raw tokens,
// since a shared message cannot show every candidate's name.
const templatePreviewMode = computed<'raw' | 'personalized'>(() =>
  props.pros.length === 1 ? 'personalized' : 'raw',
);

// Methods
// ===============================
const sendMessage = async () => {
  if (
    hasDoNotContactRecipient.value ||
    !prosWithOutreachApproved.value.length ||
    sendingMessage.value
  )
    return;
  try {
    sendingMessage.value = true;
    const project = selectedProject.value;

    if (!project) {
      throw new Error('Project is not defined');
    }

    // Route to AI or Manual flow based on toggle.
    if (aiOutreachEnabled.value && canEnableAIOutreach.value) {
      // AI outreach flow
      await outreachService.initiateManualAIOutreach(
        project,
        prosWithOutreachApproved.value,
        props.searchSessionId,
      );
    } else {
      // Manual message flow
      await messagingService.sendMessageToRecipients(
        message.value,
        project,
        prosWithOutreachApproved.value,
        props.searchSessionId,
      );
    }

    messageSentFlag.value = true;
    emit('submit');
  } catch (error) {
    // InternalError = the service already showed a specific snackbar (job inactive,
    // existing application, etc.). Skip Sentry capture and the generic toast for
    // those — only escalate truly unexpected failures.
    if (!(error instanceof InternalError)) {
      ErrorService.captureException(error);
      SnackbarService.critical('Something went wrong. Please try again.');
    }
  } finally {
    sendingMessage.value = false;
  }
};

// The back control returns to the template list from a create/edit form, and to the
// Add to Job form from the list. Closing the modal is the X, which BaseModal keeps
// separate from back.
function onModalBack() {
  if (templateSubview.isEditingTemplate.value) {
    templateLibrary.value?.showBrowse();
    return;
  }
  templateSubview.close();
}

function onTemplateInsert(template: MessageTemplate) {
  // Raw text on purpose: the recruiter can still edit it, and placeholders are resolved
  // per recipient at send time so a bulk add gives each candidate their own name.
  message.value = template.message;
  templateSubview.close();

  TrackingService.trackAction('Template Inserted', {
    template_id: template.id,
    type: template.type,
    content: template.message,
  });

  // Wait for the form to be visible again before focusing.
  setTimeout(() => {
    messageTextAreaEl.value?.focus();
  }, 500);
}

async function close({ messageSent }: { messageSent: boolean }) {
  emit('close', { messageSent });
  await nextTick();
  resetForm();

  // Wait for close animation to finish
  setTimeout(() => {
    messageSentFlag.value = false;
  }, 500);
}

function resetForm() {
  selectedJobId.value = undefined;
  message.value = '';
  templateSubview.close();
  resetFilters();
  aiOutreachEnabled.value = false;
  excludeFlaggedCandidates.value = false;
  selectedProsJobApplicants.value = [];
  jobPreselectToken += 1;
}

async function fetchSelectedProsJobApplicants() {
  const employerId = meService.employer?.id;
  if (!employerId || props.pros.length === 0) {
    selectedProsJobApplicants.value = [];
    return;
  }

  const token = ++fetchToken;
  try {
    const jobApplicants = await jobApplicantService.getJobApplicantsByUserProfileIds(
      employerId,
      props.pros.map((pro) => Number(pro.id)),
    );
    // A newer open superseded this fetch — drop the stale result.
    if (token !== fetchToken) return;
    selectedProsJobApplicants.value = jobApplicants;
  } catch (error) {
    ErrorService.captureException(error);
  }
}

// `projects` only holds the selector's first page (100 items) — an employer with more jobs
// than that could have an active/searched-from job missing from it. Fetch it directly by
// jobId rather than silently leaving the preselection empty.
async function resolveInitialJob(initialJobId: number): Promise<boolean> {
  if (projects.value.some((project) => project.jobId === initialJobId)) {
    return true;
  }
  if (props.excludedJobIds.includes(initialJobId)) {
    return false;
  }

  const employerId = meService.employer?.id;
  if (!employerId) return false;

  try {
    const { projects: matches } = await projectPersistence.searchProjects({
      employerId,
      search: { jobIds: [initialJobId], pagination: { page: 1, itemsPerPage: 25 } },
    });
    const [fetchedProject] = matches;
    if (!fetchedProject) return false;

    projects.value = [fetchedProject, ...projects.value];
    return true;
  } catch (error) {
    ErrorService.captureException(error);
    return false;
  }
}

// Watchers
// ===============================
watch(computedOpen, async (open) => {
  if (open) {
    if (recipientsCount.value === 1) {
      showMore.value = true;
    }

    // Run concurrently, but seed the preselection as soon as `projects` resolves rather
    // than waiting on both — the picker isn't disabled while loading, so waiting on the
    // (often slower) applicant-history fetch too left a window where a recruiter could
    // pick a different job and have this seed silently overwrite their choice.
    const applicantsPromise = fetchSelectedProsJobApplicants();
    await fetchProjects();

    // Seed only if nothing has changed the selection in the meantime (the modal is still
    // open and the recruiter hasn't already picked something), and only if the job is
    // actually present in the (possibly excludedJobIds-filtered) list — a phantom
    // selection here would leave submit enabled with no matching `selectedProject`, and
    // the AI-outreach-default watcher below would already have run against an empty list.
    if (computedOpen.value && selectedJobId.value === undefined) {
      const initialJobId = props.initialJobId;
      if (initialJobId !== undefined) {
        const token = jobPreselectToken;
        const resolved = await resolveInitialJob(initialJobId);
        // A close (resetForm) happened while resolveInitialJob was in flight — this session
        // is gone, and a same-instance reopen already got its own fresh token. Writing now
        // would leak this stale job into whatever's current instead.
        if (token !== jobPreselectToken) return;
        selectedJobId.value = resolved ? initialJobId : undefined;
      }
    }

    await applicantsPromise;
  }
});

watch(hasDoNotContactRecipient, (blocked) => {
  if (blocked) aiOutreachEnabled.value = false;
});

// AI outreach takes the manual message field away, so the template library must not
// stay open over it.
watch(canUseTemplates, (usable) => {
  if (!usable) {
    templateSubview.close();
  }
});

watch(selectedJobId, (newJobId) => {
  if (newJobId && canEnableAIOutreach.value) {
    aiOutreachEnabled.value = true; // Default to ON for active jobs
  } else {
    aiOutreachEnabled.value = false; // OFF for inactive or no job
  }
});
</script>

<template>
  <BaseModal
    v-model="computedOpen"
    :is-loading="sendingMessage"
    :is-submit-disabled="disableSendButton"
    :show-close-button="true"
    :show-cancel-button="true"
    :fullscreen-on-mobile="true"
    :show-heading="!messageSentFlag"
    :show-actions="!messageSentFlag && !templateSubview.isOpen.value"
    :title="templateSubview.title.value"
    :subtitle="templateSubview.subtitle.value"
    :back-button-text="templateSubview.backButtonText.value"
    submit-button-text="Add to job"
    @submit="sendMessage"
    @cancel="close({ messageSent: false })"
    @back="onModalBack"
  >
    <MessageSentAnimation
      v-if="messageSentFlag"
      @animation:complete="close({ messageSent: true })"
    />

    <div v-else>
      <div v-show="!templateSubview.isOpen.value" data-test="add-pro-to-job-form">
        <!-- Single-candidate identity: closing the resume to get here drops that context
           from view, so name the candidate again inside the modal. -->
        <div v-if="pros.length === 1" class="sensitive mt-2 text-sm font-semibold text-shade-900">
          {{ pros[0].nameFirst }} {{ pros[0].nameLast }}
        </div>

        <!-- Job Selector -->
        <div class="mt-5">
          <span class="text-sm font-bold">Select job*</span>
          <div class="mt-1">
            <Autocomplete
              v-model="selectedJobId"
              v-model:search="projectSearch"
              :disabled="sendingMessage"
              :loading="isLoadingProjects"
              :items="projects"
              :plain="true"
              :render-item="renderProject"
              clearable
              item-title="title"
              item-value="jobId"
              placeholder="Select a job"
            >
              <template #prepend-item>
                <div class="flex flex-wrap gap-x-6 gap-y-2 px-4 pb-4 pt-2">
                  <span class="text-xs text-shade-800">Filter by:</span>

                  <div class="flex items-center gap-2">
                    <Checkbox v-model="myJobsFilter"></Checkbox>
                    <span class="text-xs text-shade-800">My jobs</span>
                  </div>

                  <div class="flex items-center gap-2">
                    <Checkbox v-model="copilotJobsFilter"></Checkbox>
                    <span class="text-xs text-shade-800">Active jobs</span>
                  </div>

                  <div class="flex items-center gap-2">
                    <Checkbox v-model="atsJobsFilter"></Checkbox>
                    <span class="text-xs text-shade-800">ATS jobs</span>
                  </div>
                </div>

                <Divider class="mb-1" />
              </template>
            </Autocomplete>
          </div>
        </div>

        <RecentProOutreachWarning
          v-if="showRecentProOutreachWarning"
          :warning="recentProOutreachWarning"
          v-model:exclude-flagged="excludeFlaggedCandidates"
        />

        <!-- AI Outreach Toggle -->
        <div class="mt-6 rounded-xl border border-solid border-[#D8BEFF] bg-highlight-0 px-4 py-3">
          <!-- Label and Toggle -->
          <div class="flex h-[21px] items-center justify-start">
            <span class="text-sm font-bold leading-[21px] text-[#111928]">
              Let your AI recruiter handle outreach
            </span>
            <v-tooltip
              :text="aiOutreachTooltipText"
              :disabled="canEnableAIOutreach || !aiOutreachTooltipText"
              location="top"
              offset="-4"
              content-class="manual-outreach-tooltip tooltip-bottom-arrow"
            >
              <template v-slot:activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps" class="ml-2 mt-[8px]">
                  <v-switch
                    v-model="aiOutreachEnabled"
                    class="add-candidate-to-job-switch !inline-block"
                    hide-details
                    density="compact"
                    :disabled="!canEnableAIOutreach"
                  />
                </div>
              </template>
            </v-tooltip>
          </div>

          <div class="mt-2 text-sm font-normal leading-[21px] text-shade-820">
            Candidates will be messaged about the role and assessed for fit. Outreach is queued and
            may not start immediately.
          </div>
        </div>

        <!-- Textarea -->
        <div v-if="showMessageField" class="mt-5">
          <span class="text-sm font-bold">Send first message</span>
          <span class="text-sm text-[#979797]">&nbsp;(optional)</span>
          <textarea
            ref="messageTextAreaEl"
            v-model="message"
            :disabled="sendingMessage || messageFieldDisabled"
            :class="{ 'cursor-not-allowed opacity-50': messageFieldDisabled }"
            class="mt-1 w-full resize-none rounded-md border border-tint-80 px-4 py-4 text-shade-900 placeholder:text-sm placeholder:text-shade-800 focus:outline-none focus:ring-2 focus:ring-highlight-500"
            placeholder="Type your message here"
            rows="4"
          />

          <div v-if="canUseTemplates" class="mt-4">
            <v-btn
              class="modal-button-secondary"
              :ripple="false"
              variant="flat"
              data-test="use-a-template"
              @click="templateSubview.open"
            >
              <PlusCircleSvg /> Use a template
            </v-btn>
          </div>
        </div>

        <div class="mt-5"></div>
      </div>

      <!-- Template library: a subview of this modal rather than a second stacked dialog,
           so the Add to Job form above keeps its state while it is open. -->
      <TemplateLibrary
        v-if="templateSubview.isOpen.value"
        ref="templateLibrary"
        :preview-mode="templatePreviewMode"
        :preview-context="templatePreviewContext"
        :allow-send="false"
        :allow-manage="true"
        @insert="onTemplateInsert"
        @mode-change="templateSubview.setMode"
      />
    </div>
  </BaseModal>
</template>

<style>
.manual-outreach-tooltip {
  border-radius: 8px !important;
  padding: 8px !important;
  background-color: #0f172a !important;
}

.add-candidate-to-job-switch {
  .v-switch__track {
    width: 24px !important;
    height: 12px !important;
    background: #94a3b8 !important;
    opacity: 1 !important;
  }
  .v-selection-control--dirty {
    .v-switch__track {
      @apply bg-highlight-500 !important;
      opacity: 1 !important;
    }
  }
  .v-switch__thumb {
    width: 16px !important;
    height: 16px !important;
    box-shadow: none !important;
    border: 0.5px solid #94a3b8;
  }
  .v-selection-control__input::before {
    opacity: 0 !important;
  }
  .v-ripple__container {
    display: none;
  }
  .v-selection-control__wrapper {
    @apply ml-2;
  }
}
</style>
